import React, { memo, useEffect, useMemo, useRef } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import PLAYER_TYPES from 'constants/player-types';
import SpatialNavigationWrapper from 'components/navigation/SpatialNavigationWrapper';
import { getNavigationButtonConfig } from './utils';
import i18n from 'i18n';
import {
  FavoriteButton,
  PrevButton,
  PlayButton,
  NextButton,
  SettingButton,
  EPGButton,
  OnAirButton,
} from './Buttons';
import { StyledButtonsWrapper } from './StyledComponents';
import { StyledOnAirLabel } from 'components/Player/StyledComponents';
import { ReactComponent as OnAirIcon } from 'assets/img/player-icon-onair.svg';

const ON_AIR_BUTTON_ID = 'onAirButton';

const WITH_EPG = [
  PLAYER_TYPES.CHANNEL,
  PLAYER_TYPES.VOD_CHANNEL,
  PLAYER_TYPES.CATCHUP,
  PLAYER_TYPES.TIMESHIFT,
];

const ControlsSet = (props) => {
  const { defaultSection, sectionName, onSectionFocus, playerType, content, navigation } = props;
  const { isLive, isResetToStartAvailable, isSettingsAvailable, isPlaying, idle } = props;
  const {
    toggleInFavoriteStatus,
    onClickPrev,
    onClickPause,
    onClickPlay,
    onClickSettings,
    onClickNext,
    onClickEPG,
    onClickOnAir,
  } = props;

  const styledButtonsWrapperRef = useRef(null);

  const isInFavorite = useMemo(() => {
    const isCurrentItemInFavorite = get(content, ['favorite']);
    if (playerType === PLAYER_TYPES.CATCHUP) {
      return isCurrentItemInFavorite || get(content, ['channelMetadata', 'favorite']);
    }
    if (playerType === PLAYER_TYPES.SERIES) {
      return get(content, ['seriesMetadata', 'favorite']);
    }
    return isCurrentItemInFavorite;
  }, [content, playerType]);

  const prevButtonProps = useMemo(
    () =>
      getNavigationButtonConfig({
        buttonType: 'prev',
        playerType,
        navigation,
        isResetToStartAvailable,
      }),
    [playerType, navigation, isResetToStartAvailable],
  );
  const nextButtonProps = useMemo(
    () => getNavigationButtonConfig({ buttonType: 'next', playerType, navigation, isLive }),
    [playerType, navigation, isLive],
  );

  const defaultFocusElementID = [
    PLAYER_TYPES.CHANNEL,
    PLAYER_TYPES.CATCHUP,
    PLAYER_TYPES.VOD_CHANNEL,
    PLAYER_TYPES.TIMESHIFT,
  ].includes(playerType)
    ? '#EPG_BUTTON'
    : '#PLAY_BUTTON';

  const leaveForConfig = {
    up: playerType === PLAYER_TYPES.CHANNEL ? '' : '@PROGRESS_BAR',
  };

  const CONFIG = {
    defaultElement: defaultFocusElementID,
    enterTo: 'default-element',
    leaveFor: leaveForConfig,
  };

  useEffect(() => {
    // Пытаемся вернуть фокус обратно на контролы после того,
    // как прогресс бар был удалён из дома и добавлен обратно
    const domNode = findDOMNode(styledButtonsWrapperRef.current);
    SpatialNavigationWrapper.removeFocusable(sectionName);
    SpatialNavigationWrapper.addFocusable(domNode, {
      config: CONFIG,
      sectionName,
      defaultSection: true,
      setAutoFocus: !idle,
    });
    if (playerType === PLAYER_TYPES.CHANNEL && !idle) {
      SpatialNavigationWrapper.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerType]);

  // STVWL-488
  // [220] - VOD В момент перемикання в ефір, якщо навести фокус на Назадо до ефіру - зникають всі контроли
  useEffect(() => {
    if (isLive && document.activeElement.id === ON_AIR_BUTTON_ID) {
      SpatialNavigationWrapper.focus();
    }
  }, [isLive]);

  return (
    <StyledButtonsWrapper
      ref={styledButtonsWrapperRef}
      defaultSection={defaultSection}
      sectionName={sectionName}
      onFocus={onSectionFocus}
    >
      <OnAirButton
        id={ON_AIR_BUTTON_ID}
        onEnter={onClickOnAir}
        disabled={isLive}
        isHidden={
          isLive ||
          [PLAYER_TYPES.VOD, PLAYER_TYPES.SERIES, PLAYER_TYPES.TRAILER].includes(playerType)
        }
        text={i18n.t('player.buttons.backToAir')}
      />
      {isLive && (
        <StyledOnAirLabel>
          <OnAirIcon />
          {i18n.t('player.buttons.onAir')}
        </StyledOnAirLabel>
      )}
      <PrevButton onEnter={onClickPrev} {...prevButtonProps} />
      {WITH_EPG.includes(playerType) && (
        <EPGButton id={'EPG_BUTTON'} onEnter={onClickEPG} hint={i18n.t('player.buttons.epgHint')} />
      )}
      <NextButton onEnter={onClickNext} {...nextButtonProps} />
      <PlayButton
        onEnter={isPlaying ? onClickPause : onClickPlay}
        disabled={[PLAYER_TYPES.CHANNEL].includes(playerType)}
        isHidden={[PLAYER_TYPES.CHANNEL].includes(playerType)}
        isPlaying={isPlaying}
        id={'PLAY_BUTTON'}
        className={!WITH_EPG.includes(playerType) ? 'movie' : ''}
      />
      <FavoriteButton
        disabled={!content || playerType === PLAYER_TYPES.TRAILER}
        isHidden={!content || playerType === PLAYER_TYPES.TRAILER}
        isActive={isInFavorite}
        onEnter={toggleInFavoriteStatus}
      />
      <SettingButton onEnter={onClickSettings} disabled={!isSettingsAvailable} />
    </StyledButtonsWrapper>
  );
};

ControlsSet.defaultProps = {
  defaultSection: true,
};

ControlsSet.propTypes = {
  sectionName: PropTypes.string.isRequired,
  onSectionFocus: PropTypes.func.isRequired,
  playerType: PropTypes.string.isRequired,
  toggleInFavoriteStatus: PropTypes.func.isRequired,
  onClickPrev: PropTypes.func.isRequired,
  onClickRewind: PropTypes.func.isRequired,
  onClickPause: PropTypes.func.isRequired,
  onClickPlay: PropTypes.func.isRequired,
  onClickFF: PropTypes.func.isRequired,
  onClickSettings: PropTypes.func.isRequired,
  onClickNext: PropTypes.func.isRequired,
  defaultSection: PropTypes.bool,
  isResetToStartAvailable: PropTypes.bool,
  isSettingsAvailable: PropTypes.bool,
  isPlaying: PropTypes.bool,
  navigation: PropTypes.object,
};

export default memo(ControlsSet);
